import { Box, CircularProgress, makeStyles } from '@material-ui/core';
import { Add, Delete, Visibility } from '@material-ui/icons';
import { Image, Upload } from 'antd';
import React from 'react';
import clsx from 'clsx';
import { getImageUrl } from '../../../services/util/image';
import useUploadFile from '../../hooks/useUploadFile';

const MultipleFilesUploader = React.forwardRef(({ value, onChange, maxCount = 4, error, disabled }, ref) => {
  const { uploadFile, isLoading } = useUploadFile();

  const classes = useStyles({
    error,
  });

  return (
    <div className={classes.wrapper}>
      {value?.map?.((url) => (
        <div className={classes.imagePreview}>
          <Image
            key={url}
            src={getImageUrl(url)}
            alt="proof"
            wrapperClassName={classes.imageWrapper}
            className={classes.image}
            preview={{
              mask: (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    gap: 8,
                  }}
                >
                  <Visibility className={classes.maskIcon} />
                  {!disabled && (
                    <Delete
                      className={clsx(classes.maskIcon, classes.errorText)}
                      onClick={(e) => {
                        e.preventDefault();
                        onChange?.(value.filter((item) => item !== url));
                      }}
                    />
                  )}
                </Box>
              ),
              imageRender: (image) => image,
            }}
          />
        </div>
      ))}
      {value?.length >= maxCount ? null : (
        <Upload
          ref={ref}
          isLoading={isLoading}
          action={async (file) => {
            const fileKey = await uploadFile(file);
            if (fileKey) {
              onChange?.(value.concat(fileKey));
            }
          }}
          showUploadList={false}
          className={classes.upload}
          accept="image/*"
          disabled={isLoading}
        >
          <Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
            {isLoading ? <CircularProgress size={30} /> : <Box component={Add} width={30} height={30} />}
          </Box>
        </Upload>
      )}
    </div>
  );
});
MultipleFilesUploader.displayName = 'MultipleFilesUploader';

export default MultipleFilesUploader;

const useStyles = makeStyles((theme) => ({
  messageText: {
    color: theme.palette.grey[500],
    fontSize: '0.75rem',
    lineHeight: 1.66,
  },
  errorText: {
    color: theme.palette.error.main,
  },
  uploadIcon: ({ error }) => ({
    width: 32,
    height: 32,
    color: error ? theme.palette.error.main : theme.palette.grey[600],
  }),
  imagePreview: {
    display: 'grid',
    padding: 4,
    border: `1px dashed ${theme.palette.primary[500]}`,
    background: theme.palette.primary[100],
    borderRadius: 10,
    width: '100%',
    placeItems: 'center',
  },
  image: {
    borderRadius: 8,
    height: '100% !important',
    objectPosition: 'center',
    objectFit: 'contain',
  },
  upload: ({ error }) => ({
    width: '100%',
    aspectRatio: '1/1',
    borderRadius: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.palette.grey[100],
    color: theme.palette.grey[500],
    border: error ? `1px dashed ${theme.palette.error.main}` : `1px dashed ${theme.palette.grey[500]}`,
    '&:hover': {
      border: `1px dashed ${theme.palette.primary.main}`,
      background: theme.palette.primary[100],
      cursor: 'pointer',
      color: theme.palette.primary.main,
    },
    '& .ant-upload-select': {
      width: '100%',
      height: '100%',
      display: 'block',
    },
  }),
  maskIcon: {
    width: 20,
    height: 20,
    transition: 'transform',
    '&:hover': {
      transform: 'scale(1.1)',
      cursor: 'pointer',
    },
  },
  wrapper: {
    display: 'grid',
    gap: 10,
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));
